import React, { useEffect, useRef, useState } from 'react';
import { DOWN_ARROW_KEY, ENTER_KEY, ESCAPE_KEY, SPACE_KEY, TAB_KEY, UP_ARROW_KEY } from '../../../const/keyNames';
import { dispatchEvent, isOptionEqual, useOutsideAlerter, useUpdateMenuPosition } from './helpers';
import OptionsMenu from './OptionsMenu';
import { FieldWrapper, HelpAndError, HelpTextWrapper, NativeSelect, SelectInnerWrapper, SelectOuterWrapper, SelectValue } from './subcomponents';
const Select = ({ id, name, error = '', options = [], placeholder = '', helpText = '', leftIcon = '', defaultOption, rightIconOpen = '', rightIconClosed = '', errorIcon = '', isDisabled = false, className = '', onChange = () => { }, onBlur = () => { }, placement = 'BOTTOM', autoAdjust, required = false, tabIndex = 0, selectInputRef, dataCy, dataType, dataObject, dataReason, dataAnalytics }) => {
    const [isMenuOpen, setMenuOpen] = useState(false);
    const wrapperRef = useRef(null);
    const nativeSelectRef = useRef(null);
    const animateLabel = !isDisabled || error;
    const [selectedOption, setSelectedOption] = useState(defaultOption);
    const [focusedOption, setFocusedOption] = useState(selectedOption);
    const [shouldChangeBeDispatched, setChangeBeDispatched] = useState(false);
    const hasSelection = !!(selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.label);
    const [menuPosition, setMenuPosition] = useState(placement);
    const defaultRef = useRef(null);
    const ref = selectInputRef !== null && selectInputRef !== void 0 ? selectInputRef : defaultRef;
    const selectAriaLabelledBy = `${id}-label`;
    // Call back for positioning the menu placement based on bottom space and field position
    const getMenuPosition = useUpdateMenuPosition(options, setMenuPosition, isMenuOpen, autoAdjust);
    useOutsideAlerter(wrapperRef, () => setMenuOpen(false));
    const selectedOptionLabel = (selectedOption && selectedOption.label) || placeholder;
    const toggleOpen = () => setMenuOpen(prevMenuOpen => !prevMenuOpen);
    const moveFocusUp = () => {
        // We firstly need to get the index of the current focused option
        const currFocusedOptionIndex = options.findIndex(option => isOptionEqual(option, focusedOption));
        /**
         * When we press arrow UP, we need to move the focus to the option ABOVE the current one, so we want the focusedOptionIndex - 1
         * and we need to make sure we don't get a number smaller than 0 (because that wouldn't be a valid index in the array of options)
         */
        const newFocusedOptionIndex = currFocusedOptionIndex > 1 ? currFocusedOptionIndex - 1 : 0;
        const newFocusedOption = options[newFocusedOptionIndex];
        setFocusedOption(newFocusedOption);
    };
    const moveFocusDown = () => {
        // We firstly need to get the index of the current focused option
        const currFocusedOptionIndex = options.findIndex(option => isOptionEqual(option, focusedOption));
        /**
         * When we press arrow DOWN, we need to move the focus to the option BELOW the current one, so we want the focusedOptionIndex + 1
         * and we need to make sure we don't get a number greater than the last option index (because that wouldn't be a valid index in the array of options)
         */
        const lastOptionIndex = options.length - 1;
        const newFocusedOptionIndex = currFocusedOptionIndex + 1 <= lastOptionIndex
            ? currFocusedOptionIndex + 1
            : lastOptionIndex;
        const newFocusedOption = options[newFocusedOptionIndex];
        setFocusedOption(newFocusedOption);
    };
    const selectOption = (option) => {
        if (option.onClick) {
            option.onClick();
        }
        setSelectedOption(option);
        toggleOpen();
    };
    const performAction = (key) => {
        var _a;
        switch (key) {
            case UP_ARROW_KEY:
                moveFocusUp();
                break;
            case DOWN_ARROW_KEY:
                moveFocusDown();
                break;
            case TAB_KEY:
                setSelectedOption(focusedOption);
                toggleOpen();
                break;
            case ESCAPE_KEY:
                toggleOpen();
                (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.focus();
                break;
            case ENTER_KEY:
            case SPACE_KEY:
                selectOption(focusedOption);
                break;
            default:
                break;
        }
    };
    const handleWrapperOnKeyDown = (event) => {
        if (event.key !== TAB_KEY)
            event.preventDefault();
        const openKeys = [UP_ARROW_KEY, DOWN_ARROW_KEY, ENTER_KEY, SPACE_KEY];
        if (!isMenuOpen && openKeys.includes(event.key)) {
            toggleOpen();
        }
        else if (isMenuOpen) {
            performAction(event.key);
            event.stopPropagation();
        }
    };
    useEffect(() => {
        const hasDefaultOptionChanged = !isOptionEqual(selectedOption, defaultOption);
        if (defaultOption && hasDefaultOptionChanged) {
            setSelectedOption(defaultOption);
        }
        /*
         * We don't want to trigger a 'change' event when the default option is being set,
         * because no real 'change' event has happened. We start dispatching after first mount,
         * when this variable has been enabled..
         */
        setChangeBeDispatched(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultOption]);
    useEffect(() => {
        /*
         * the onChange callback prop is passed to the native select element,
         * but since we don't actually use that element to select a different
         * option we need to manually trigger an onChange event on it for the
         * callback to be executed
         */
        dispatchEvent(nativeSelectRef, shouldChangeBeDispatched);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOption, id]);
    return (React.createElement(FieldWrapper, { onKeyDown: handleWrapperOnKeyDown, ref: wrapperRef, "data-cy": dataCy },
        React.createElement(NativeSelect, { id: `native-select-${id}`, nativeSelectRef: nativeSelectRef, name: name, required: required, error: error, tabIndex: tabIndex, onChange: onChange, options: options, selectedOption: selectedOption, dataCy: dataCy }),
        React.createElement(SelectOuterWrapper, { ref: getMenuPosition },
            React.createElement(SelectInnerWrapper, { ref: ref, error: error, isDisabled: isDisabled, "aria-required": required, "aria-disabled": isDisabled, isMenuOpen: isMenuOpen, placement: menuPosition, tabIndex: tabIndex, "aria-expanded": isMenuOpen, "data-type": dataType, "data-object": dataObject, "data-reason": dataReason, "data-analytics": dataAnalytics, className: `Select__inner-wrapper ${className}`, onClick: () => !isDisabled && setMenuOpen(!isMenuOpen), role: "combobox", onBlur: onBlur, "aria-labelledby": selectAriaLabelledBy, "aria-haspopup": "listbox", "aria-controls": `${id}-options-inner-wrapper`, id: `${id}-combobox` },
                React.createElement(SelectValue, { isMenuOpen: isMenuOpen, animateLabel: animateLabel, hasSelection: hasSelection, selectedOptionLabel: selectedOptionLabel, placeholder: placeholder, id: id, name: name, dataCy: dataCy, leftIcon: leftIcon, rightIconClosed: rightIconClosed, rightIconOpen: rightIconOpen, isDisabled: isDisabled, error: error })),
            isMenuOpen && (React.createElement(OptionsMenu, { menuPosition: menuPosition, options: options, selectedOption: selectedOption, focusedOption: focusedOption, onOptionClick: selectOption, id: id, ariaLabelledBy: selectAriaLabelledBy }))),
        React.createElement(HelpTextWrapper, { error: error, "aria-live": "polite", "data-cy": dataCy && `${dataCy}.helptext` }, (error || helpText) && (React.createElement(HelpAndError, { id: id, error: error, helpText: helpText, errorIcon: errorIcon })))));
};
export default Select;
